import type { CSSProperties } from "react";

/**
 * TUT-539 — A COR DO TIPO, o novo padrão do Lumen (D2/D5/D6 do dono, 23/10).
 *
 * A cliente pediu, na reunião de 23/10 (M24), a cor do TIPO no cartão
 * inteiro, "não numa bolinha": quesito amarelo, perícia azul, laudo verde,
 * impugnação vermelho, reunião cinza e consultoria laranja. O Lumen 1.0 dizia
 * o contrário (a cor como acento, "a paleta por assunto atual fica como
 * está"), e o dono reviu de propósito, escolhendo na prévia:
 *
 * - a preview A, **a tinta inteira**: o cartão numa tinta clara do tipo (no
 *   máximo 20%), a borda na cor e um único sólido, o selo (o ladrilho);
 * - a paleta R: quesito mostarda, perícia no azul de sempre, laudo
 *   verde-folha (sai do lilás), impugnação framboesa (longe do carmim de
 *   "crítico"), reunião ardósia e consultoria no laranja que era de prazos.
 *
 * **As travas contra o arco-íris:** só estes seis tipos pintam; a tinta é de
 * no máximo 20% e o texto que mora nela é medido (`contrast.test.ts`); o
 * sólido é um só, o selo; o ESTADO nunca pinta, é ponto e palavra (o ponto
 * continua na cor da situação dentro do cartão tingido); painéis, cabeçalhos e
 * menu ficam neutros; e o item sem tipo (o prazo, a proposta) não ganha tinta.
 *
 * **Uma regra só para os seis tipos (o orçamento do CSS de entrada).** O tema
 * guarda UM token por tipo (a cor) e a PORÇÃO da mistura, uma para todos
 * (`--tipo-tinta`, que muda com o tema, e `--tipo-borda`). O que só muda com o
 * tipo vai em linha, onde não custa CSS: a tinta do fundo (`estiloDoTipo`) e
 * o sólido do selo (`estiloDoSelo`). A borda é a única classe
 * (`tintaDoTipoClass`), porque o estado da peça a troca (abaixo). A primeira
 * versão desta fatia gerava uma regra por tipo e repetia a tinta por tema:
 * 2.778 bytes a mais no CSS de entrada, que estourou o orçamento; a conta de
 * cada versão está no `check-bundle-budget.mjs`.
 *
 * Os valores moram no tema (`theme-brevi.css`), e só esta receita os escreve
 * (a trava (g) do `acabamento-leve.test.ts`): a tela passa `tipo` ao cartão de
 * quadro, ao compromisso, à linha da lista e ao ladrilho.
 */

/** Os seis tipos que pintam, na ordem do Canva da cliente. */
export const TIPOS_DE_ITEM = ["quesito", "pericia", "laudo", "impugnacao", "reuniao", "consultoria"] as const;

export type TipoDeItem = (typeof TIPOS_DE_ITEM)[number];

/** O token de cada tipo, um só: a cor dele, o sólido do selo. */
export const CORES_DO_TIPO: Record<TipoDeItem, string> = {
  quesito: "var(--tipo-quesito)",
  pericia: "var(--tipo-pericia)",
  laudo: "var(--tipo-laudo)",
  impugnacao: "var(--tipo-impugnacao)",
  reuniao: "var(--tipo-reuniao)",
  consultoria: "var(--tipo-consultoria)",
};

/**
 * A PALAVRA do tipo, a que o selo escreve (o chip sólido da prévia A,
 * decisão do dono em 24/10).
 */
export const ROTULO_DO_TIPO: Record<TipoDeItem, string> = {
  quesito: "Quesito",
  pericia: "Perícia",
  laudo: "Laudo",
  impugnacao: "Impugnação",
  reuniao: "Reunião",
  consultoria: "Consultoria",
};

/** A porção do tipo na TINTA do fundo (a do tema: ver `theme-brevi.css`). */
export const PORCAO_DA_TINTA = "var(--tipo-tinta)";
/** A porção do tipo na BORDA (60%, como na prévia). */
export const PORCAO_DA_BORDA = "var(--tipo-borda)";

/**
 * O tipo da superfície (cartão de quadro, compromisso, linha da lista), em
 * linha: a TINTA do fundo e a cor da BORDA, cada uma a mistura do token do
 * tipo com a porção do tema.
 *
 * - A tinta é IMAGEM (um degradê de uma cor só), por cima do fundo que a peça
 *   já tem: o vidro forte do cartão fica embaixo dela, em vez de trocado; no
 *   compromisso e na linha, que não têm fundo próprio, ela pousa direto no
 *   painel. Nenhuma classe de estado troca a imagem de fundo, então ela vai
 *   em linha.
 * - A borda NÃO vai em linha, porque o selecionado (`border-primary`), o
 *   arraste e o hover a trocam, e o estilo em linha venceria as classes deles.
 *   Ela vai como variável, que a classe `tintaDoTipoClass` lê.
 */
export function estiloDoTipo(tipo: TipoDeItem | null | undefined): CSSProperties | undefined {
  if (!tipo) return undefined;
  const cor = CORES_DO_TIPO[tipo];
  return {
    backgroundImage: `linear-gradient(color-mix(in srgb, ${cor} ${PORCAO_DA_TINTA}, transparent) 0 0)`,
    "--borda-do-tipo": `color-mix(in srgb, ${cor} ${PORCAO_DA_BORDA}, transparent)`,
    "--cor-do-tipo": cor,
  } as CSSProperties;
}

/** O SÓLIDO do selo (o fundo do ladrilho), em linha: o único sólido do cartão. */
export function estiloDoSelo(tipo: TipoDeItem | null | undefined): CSSProperties | undefined {
  return tipo ? { backgroundColor: CORES_DO_TIPO[tipo] } : undefined;
}

/**
 * A BORDA da superfície tingida, a parte da tinta que é classe: lê a cor que
 * o `estiloDoTipo` dá. Vem no `cn` antes das classes de estado, que a vencem.
 * A peça põe a largura da borda (`border`).
 */
export const tintaDoTipoClass = "border-[color:var(--borda-do-tipo)]";

/**
 * O HOVER do cartão de quadro tingido: a borda vai ao sólido do tipo (o
 * `--cor-do-tipo` do `estiloDoTipo`). O casco do cartão hoverava em `--accent`,
 * que é o laranja da consultoria; esta classe vem depois dele no `cn` e o
 * vence. Só os quadros a somam: o compromisso e a linha da lista não têm
 * hover de borda.
 */
export const hoverDoTipoClass = "hover:border-[color:var(--cor-do-tipo)]";

/** A classe da tinta de um item, ou nada: o item sem tipo (o prazo) não ganha tinta. */
export function tintaDoTipo(tipo: TipoDeItem | null | undefined): string | undefined {
  return tipo ? tintaDoTipoClass : undefined;
}
